import React, { useEffect, useState } from 'react';
import type { AuthUser } from '../../types';
import { useParams, Navigate } from 'react-router-dom';
import ModuleRouter from './ModuleRouter';
import { useTheme } from '../../theme/ThemeContext';
import { useChurchConfig } from '../../hooks';
import { hasModuleAccess, isReadOnly } from '../../utils/auth';
import { ReadOnlyContext } from '../../utils/readOnly';
import { parseHiddenModules } from '../../utils/churchSettings';
import { moduleIdForRoute, SETTINGS_MODULE_ID } from '../../routes';
import { isAdmin as checkAdmin } from '../../utils/auth';
import ModuleApprovals from '../../features/approvals/ModuleApprovals';

// Resolves /:moduleRoute to a module, then enforces permission before
// rendering. Typing a URL you have no access to sends you home rather than
// exposing the screen — the dashboard grid already hides it.
export interface ModuleRouteProps {
  user: AuthUser | null;
  /** Fixed slug for a route whose own params mean something else (settings/:section). */
  moduleRoute?: string;
}

const ModuleRoute = ({ user, moduleRoute: fixedRoute }: ModuleRouteProps) => {
  const params = useParams();
  const { isDarkMode } = useTheme();
  const config = useChurchConfig();
  // A decision carried out from the strip changes the module's records:
  // re-mount the screen so it loads them again — but not while someone has a
  // form or dialog open, which would throw their typing away. Then it waits
  // until they close it.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let timer: number | undefined;
    const busy = () => Boolean(document.querySelector('form[data-edit-form], [role="dialog"]'));
    const reload = () => {
      window.clearInterval(timer);
      if (!busy()) { setReloadKey((k) => k + 1); return; }
      timer = window.setInterval(() => {
        if (busy()) return;
        window.clearInterval(timer);
        setReloadKey((k) => k + 1);
      }, 1000);
    };
    window.addEventListener('approvals-applied', reload);
    return () => {
      window.removeEventListener('approvals-applied', reload);
      window.clearInterval(timer);
    };
  }, []);

  const moduleId = moduleIdForRoute(fixedRoute ?? params.moduleRoute);
  if (!moduleId) return <Navigate to="/" replace />;

  const permitted = moduleId === SETTINGS_MODULE_ID
    ? checkAdmin(user)
    : hasModuleAccess(user, moduleId);

  // A module the parish switched off under Settings › Modules is gone for
  // everyone, by URL as well as from the dashboard.
  if (!permitted || parseHiddenModules(config).includes(moduleId)) {
    return <Navigate to="/" replace />;
  }

  // Read Only grants reach the module, but every shared write control inside
  // it disables itself. Settings is admin-only, so it is never read-only.
  const readOnly = moduleId !== SETTINGS_MODULE_ID && isReadOnly(user, moduleId);

  return (
    <ReadOnlyContext.Provider value={readOnly}>
      {/* What in this module waits for approval (Settings › Approvals). */}
      {moduleId !== SETTINGS_MODULE_ID && <ModuleApprovals moduleId={moduleId} isDarkMode={isDarkMode} />}
      <ModuleRouter key={reloadKey} moduleId={moduleId} isDarkMode={isDarkMode} />
    </ReadOnlyContext.Provider>
  );
};

export default ModuleRoute;
